import {objectFromEntries} from '@benchling/js-utils';
import PropTypes from 'prop-types';
import React from 'react';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import FileBrowserActions, {FileOpenParams} from 'file-browser/FileBrowserActions';
import {isSchemaTag, Tag} from 'folder-items/FileConstants';
import {InternalLink} from 'notebook/mediocre/MediocreConstants';
import {getPrimaryRegistryIdentifier} from 'registry/RegistryIdentifierUtil';
import {FIELD_TYPES} from 'schemas/fields/SchemaFieldConstants';
import getValidationErrorText from 'schemas/getValidationErrorText';
import * as TagUtil from 'schemas/tags/TagUtil';
import * as TagSchemaUtil from 'tag-schema/util/TagSchemaUtil';
import {ChipSize} from 'ui-library/chips/Chip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {capitalize} from 'util/NameUtil';
import {BasicEvent} from 'util/Types';
import {WorklistType, worklistTypeDisplayName} from 'worklists/WorklistConstants';

import {StorableChipSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipSummaryGraphQL';
import {BioentityChipSummaryGraphQL} from '../../../graphql-fragments/registry/registry-items/__generated/BioentityChipSummaryGraphQL';
import ChipPopoverContent, {ChipPopoverInfo} from './ChipPopoverContent';
import {ChipItem, SmartChipType} from './useSmartChipQuery';

export interface SharedSmartChipProps<T extends SmartChipType> {
  className?: string | null;
  id?: string | null;
  isInaccessible?: boolean | null;
  isLink?: boolean | null;
  negative?: boolean | null;
  onClick?: (e: BasicEvent, item: ChipItem<T>) => void;
  onBlur?: (e: BasicEvent) => void;
  onFocus?: (e: BasicEvent) => void;
  onKeyDown?: (e: BasicEvent) => void;
  onRemove?: (e: BasicEvent) => void;
  size: ChipSize;
  tooltipTitleOverride?: string | null;
  displayNameOverride?: string | null;
  iconOverride?: BenchlingIconName | null;
  getSecondaryText?: (item: ChipItem<T>) => string | null;
}

export const SHARED_SMART_CHIP_PROPS = {
  className: PropTypes.string,
  id: PropTypes.string,
  isInaccessible: PropTypes.bool,
  isLink: PropTypes.bool,
  negative: PropTypes.bool,
  onClick: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
  onRemove: PropTypes.func,
  size: PropTypes.oneOf(Object.values(ChipSize)).isRequired,
  tooltipTitleOverride: PropTypes.string,
  displayNameOverride: PropTypes.string,
  iconOverride: PropTypes.string,
  getSecondaryText: PropTypes.func,
};

export const LOCKED_ICON_CHIP_PROPS = {iconOverride: 'lock'} as const;

export function getSharedSmartChipProps<T extends SmartChipType>(
  props: SharedSmartChipProps<T>
): SharedSmartChipProps<T> {
  return objectFromEntries(
    Object.entries(props).filter(([key, value]) => SHARED_SMART_CHIP_PROPS.hasOwnProperty(key))
  ) as SharedSmartChipProps<T>;
}

/**
 * Creates a click handler that opens files directly, bypassing URL navigation.
 *
 * By default, chips navigate to a URL (e.g. /workflows/prs_xxx) which triggers a route handler
 * that opens the file. The file opening then changes the URL to the full path (e.g. /enterprisetx/f/.../edit).
 * This double URL change in rapid succession breaks the back button - clicking back skips over both URLs.
 * This handler avoids that by opening the file directly, resulting in only one URL change.
 */
export function createDirectFileOpenClickHandler<T extends SmartChipType>(
  item: ChipItem<T> | undefined,
  onClick: ((e: BasicEvent, item: ChipItem<T>) => void) | undefined,
  handleClickLink: (e: BasicEvent) => void,
  fileOpenParams?: FileOpenParams
): (e: BasicEvent) => void {
  return (e: BasicEvent): void => {
    if (item) {
      e.stopPropagation();
      if (onClick != null) {
        handleClickLink(e);
      } else {
        FileBrowserActions.openFile(item.id, fileOpenParams);
      }
    }
  };
}

export function getTooltipTitle<T extends SmartChipType>(item: ChipItem<T>): string {
  if (APIUtil.isBiologicalEntity(item.id)) {
    return getBioentityTooltipTitle(item as BioentityChipSummaryGraphQL);
  } else if (APIUtil.isStorable(item.id)) {
    return getStorableTooltipTitle(item as StorableChipSummaryGraphQL);
  } else if (APIUtil.isParty(item.id)) {
    return item.name;
  }
  return '';
}

export function getPopoverContent<T extends SmartChipType>(item: ChipItem<T>): JSX.Element | null {
  let info: ChipPopoverInfo;
  if (APIUtil.isBiologicalEntity(item.id)) {
    info = getBioentityPopoverInfo(item as BioentityChipSummaryGraphQL);
  } else if (APIUtil.isStorable(item.id)) {
    info = getStorablePopoverInfo(item as StorableChipSummaryGraphQL);
  } else {
    return null;
  }

  return <ChipPopoverContent info={info} />;
}

function getBioentityTooltipTitleFromParts({
  name,
  primaryRegistryIdentifier,
}: {
  name: string;
  primaryRegistryIdentifier: string | null | undefined;
}): string {
  let tooltipTitle = '';
  if (primaryRegistryIdentifier) {
    tooltipTitle += `Registry ID: ${primaryRegistryIdentifier}\n`;
  }
  tooltipTitle += `Name: ${name}`;
  return tooltipTitle;
}

export function getBioentityTooltipTitle(bioentity: BioentityChipSummaryGraphQL): string {
  const primaryRegistryIdentifier = getPrimaryRegistryIdentifier(bioentity);
  return getBioentityTooltipTitleFromParts({name: bioentity.name, primaryRegistryIdentifier});
}

function getBioentityPopoverInfo(bioentity: BioentityChipSummaryGraphQL): ChipPopoverInfo {
  return {
    fields: [
      {
        label: 'Name',
        value: bioentity.name,
      },
      {
        label: 'Schema',
        value: bioentity.tagSchema?.name,
      },
      {
        label: 'Registry ID',
        value: getPrimaryRegistryIdentifier(bioentity),
      },
    ],
  };
}

export function getStorableTooltipTitle(
  storable: Pick<StorableChipSummaryGraphQL, 'barcode' | 'name'>
): string {
  let tooltipTitle = '';
  if (storable.barcode) {
    tooltipTitle += `Barcode: ${storable.barcode.value}\n`;
  }
  tooltipTitle += `Name: ${storable.name}`;
  return tooltipTitle;
}

export function getWorklistTooltipTitle(
  unreadable?: boolean,
  worklistType?: WorklistType | null,
  worklistOwnerName?: string | null
): string | undefined {
  const tooltipLines = [];
  if (unreadable) {
    tooltipLines.push("Worklist has been deleted, or you don't have access");
  }
  if (worklistType) {
    tooltipLines.push(`Type: ${capitalize(worklistTypeDisplayName(worklistType))}`);
  }
  if (worklistOwnerName) {
    tooltipLines.push(`Creator: ${worklistOwnerName}`);
  }
  return tooltipLines.length ? tooltipLines.join('\n') : undefined;
}

function getStorablePopoverInfo(
  storable: Pick<StorableChipSummaryGraphQL, 'barcode' | 'name'>
): ChipPopoverInfo {
  return {
    fields: [
      {label: 'Barcode', value: storable.barcode?.value},
      {label: 'Name', value: storable.name},
    ],
  };
}

export function getTooltipTitleFromInternalLink(internalLink: InternalLink): string | undefined {
  const {
    id,
    name,
    displayLabel,
    barcode,
    humanId,
    primaryRegistryIdentifier,
    schemaValidationState,
    validationExtras,
    worklistType,
    worklistOwnerName,
    unreadable,
  } = internalLink;
  if (!id) {
    return undefined;
  }
  const effectiveName = displayLabel || name;
  const validationTooltip = getValidationErrorText(schemaValidationState, validationExtras);
  if (validationTooltip) {
    return validationTooltip;
  }
  if (APIUtil.isBiologicalEntity(id)) {
    return getBioentityTooltipTitleFromParts({name: effectiveName, primaryRegistryIdentifier});
  }
  if (APIUtil.isStorable(id)) {
    return getStorableTooltipTitle({
      barcode: barcode != null ? {__typename: 'BarcodeNode', value: barcode} : null,
      name: effectiveName,
    });
  }
  if (
    [ModelTypeConstants.ENTRY, ModelTypeConstants.STAGE_ENTRY, ModelTypeConstants.WORKFLOW].includes(
      APIUtil.apiIdToType(id)
    )
  ) {
    let tooltipTitle = '';
    if (humanId) {
      tooltipTitle += `ID: ${internalLink.humanId}\n`;
    }
    tooltipTitle += `Name: ${effectiveName}`;
    return tooltipTitle;
  }
  if (APIUtil.isWorklist(id)) {
    return getWorklistTooltipTitle(unreadable, worklistType, worklistOwnerName);
  }
  return undefined;
}

/*
 * Returns true if chips are supported for the tag type and the tag actually has a link.
 */
export function tagShouldRenderChips(tag: Tag): boolean {
  return chipsSupportedForTagType(tag) && TagUtil.tagHasLink(tag);
}

function chipsSupportedForTagType(tag: Tag): boolean {
  return Boolean(
    isSchemaTag(tag) &&
      (TagSchemaUtil.isItemLinkFieldType(tag.tagSchemaField.fieldType) ||
        tag.tagSchemaField.fieldType === FIELD_TYPES.BLOB_LINK.type)
  );
}
